// src/pages/qc/PageQCRmAnalysis.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { RmAnalysisModal } from "./RmAnalysisModal";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { currentMonthKey } from "../../utils/dateUtils";
import { useToast } from "../../components/Toast";

// Returns "PASS" | "FAIL" | "SKIP"
function paramVerdict(measured, spec) {
  if (!measured || !spec) return "SKIP";
  const hasRange = spec.minValue != null || spec.maxValue != null;
  if (hasRange) {
    const v = parseFloat(measured);
    if (isNaN(v)) return "SKIP";
    if (spec.minValue != null && v < parseFloat(spec.minValue)) return "FAIL";
    if (spec.maxValue != null && v > parseFloat(spec.maxValue)) return "FAIL";
    return "PASS";
  }
  if (spec.expectedValue) {
    return measured.trim().toLowerCase() === spec.expectedValue.trim().toLowerCase()
      ? "PASS" : "FAIL";
  }
  return "SKIP";
}

function rowVerdict(analysis, specLookup) {
  const rmSpecs = specLookup[analysis.rmId] ?? {};
  const params  = analysis.params ?? [];
  if (params.length === 0) return "NS";

  const verdicts = params
    .map(p => paramVerdict(p.measured, rmSpecs[p.parameter]))
    .filter(v => v !== "SKIP");

  if (verdicts.length === 0) return "NS";
  if (verdicts.includes("FAIL")) return "FAIL";
  return "PASS";
}

function failedParams(analysis, specLookup) {
  const rmSpecs = specLookup[analysis.rmId] ?? {};
  return (analysis.params ?? [])
    .filter(p => paramVerdict(p.measured, rmSpecs[p.parameter]) === "FAIL")
    .map(p => p.parameter);
}

function VerdictBadge({ verdict }) {
  const cfg = {
    PASS: { bg:"#d1fae5", color:"#065f46", border:"#6ee7b7", label:"PASS" },
    FAIL: { bg:"#fee2e2", color:"#991b1b", border:"#fca5a5", label:"FAIL" },
    NS:   { bg:"#fef9c3", color:"#854d0e", border:"#fde047", label:"N/S"  },
  };
  const c = cfg[verdict] ?? cfg.NS;
  return (
    <span style={{ background:c.bg, color:c.color, border:`1px solid ${c.border}`,
      borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:600 }}>
      {c.label}
    </span>
  );
}

const ROW_BG = { PASS:"#f0fdf4", FAIL:"#fff1f2", NS:"#fefce8" };

export function PageQCRmAnalysis({ rms, myPerms }) {
  const showToast = useToast();
  const [analyses, setAnalyses] = useState([]);
  const [rmSpecs,  setRmSpecs]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState("");
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [modal,    setModal]    = useState(false);

  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    Promise.all([api.fetchRmAnalyses(), api.fetchRmSpecs()])
      .then(([analyses, specGroups]) => {
        setAnalyses(analyses);
        setRmSpecs(specGroups);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // specLookup: { rmId: { paramName: specRow } }
  const specLookup = useMemo(() => {
    const m = {};
    rmSpecs.forEach(g => {
      m[g.rmId] = {};
      (g.params ?? []).forEach(p => { m[g.rmId][p.parameter] = p; });
    });
    return m;
  }, [rmSpecs]);

  const rmMap = useMemo(() => {
    const m = {};
    rms.forEach(r => { m[r.id] = r.name; });
    return m;
  }, [rms]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return analyses.filter(a =>
      (a.date || "").startsWith(monthKey) &&
      ((a.rmId      || "").toLowerCase().includes(q) ||
       (a.invoiceNo || "").toLowerCase().includes(q) ||
       (a.batchNo   || "").toLowerCase().includes(q))
    );
  }, [analyses, search, monthKey]);

  async function handleCreate(body) {
    const created = await api.createRmAnalysis(body);
    setAnalyses(old => [created, ...old]);
    showToast("Analysis saved");
    setModal(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this analysis record?")) return;
    await api.deleteRmAnalysis(id);
    setAnalyses(old => old.filter(a => a.id !== id));
    showToast("Record deleted", "info");
  }

  const colCount = canDelete ? 12 : 11;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">RM Analysis</div>
          <div className="page-subtitle">Incoming raw material test results & pass/fail verdicts</div>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={() => setModal(true)}>+ Add Analysis</button>
        </div>
      </div>

      <div className="action-bar">
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search RM, invoice or batch…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:260 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Analysis Date</th>
                <th>RM Code</th>
                <th>Product Name</th>
                <th>Invoice No</th>
                <th>Batch No</th>
                <th>Production Date</th>
                <th>Expiry Date</th>
                <th>Density (g/mL)</th>
                <th>Parameters</th>
                <th>Verdict</th>
                <th>Remarks</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const verdict      = rowVerdict(row, specLookup);
                const failed       = failedParams(row, specLookup);
                const bg           = ROW_BG[verdict] ?? "#fff";
                const densityParam = (row.params ?? []).find(p => p.parameter.toLowerCase() === "density");
                const densitySpec  = (specLookup[row.rmId] ?? {})["Density"];
                const densityV     = paramVerdict(densityParam?.measured, densitySpec);
                const nonDensity   = (row.params ?? []).filter(p => p.parameter.toLowerCase() !== "density");
                return (
                  <tr key={row.id} style={{ background: bg }}>
                    <td style={{ whiteSpace:"nowrap" }}>{row.date}</td>
                    <td className="fw-600">{row.rmId || "—"}</td>
                    <td>{rmMap[row.rmId] || "—"}</td>
                    <td>{row.invoiceNo || "—"}</td>
                    <td>{row.batchNo   || "—"}</td>
                    <td style={{ whiteSpace:"nowrap" }}>{row.productionDate || "—"}</td>
                    <td style={{ whiteSpace:"nowrap" }}>{row.expiryDate     || "—"}</td>
                    <td style={{
                      background: densityV === "FAIL" ? "#fee2e2" : densityV === "PASS" ? "#d1fae5" : undefined,
                      fontWeight: densityV === "FAIL" ? 600 : 400,
                      color:      densityV === "FAIL" ? "#991b1b" : densityV === "PASS" ? "#065f46" : undefined,
                    }}>
                      {densityParam ? densityParam.measured : <span style={{ color:"#aaa" }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                        {nonDensity.map(p => {
                          const pv = paramVerdict(p.measured, (specLookup[row.rmId] ?? {})[p.parameter]);
                          return (
                            <span key={p.parameter} style={{
                              fontSize:11, padding:"1px 6px", borderRadius:3,
                              background: pv === "FAIL" ? "#fee2e2" : pv === "PASS" ? "#d1fae5" : "#f3f4f6",
                              color:      pv === "FAIL" ? "#991b1b" : pv === "PASS" ? "#065f46" : "#6b7280",
                              fontWeight: pv === "FAIL" ? 600 : 400,
                            }}>
                              {p.parameter}: {p.measured ?? "—"}
                            </span>
                          );
                        })}
                        {nonDensity.length === 0 && <span style={{ color:"#aaa" }}>—</span>}
                      </div>
                    </td>
                    <td>
                      <VerdictBadge verdict={verdict} />
                      {failed.length > 0 && (
                        <div style={{ fontSize:10, color:"#991b1b", marginTop:3 }}>
                          Failed: {failed.join(", ")}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize:12, color:"#6b7280", maxWidth:200, whiteSpace:"pre-wrap" }}>
                      {row.remarks || "—"}
                    </td>
                    {canDelete && (
                      <td>
                        <button className="btn-small btn-danger" onClick={() => handleDelete(row.id)}>Del</button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign:"center", color:"#aaa", padding:24 }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <RmAnalysisModal
            rms={rms}
            onSave={handleCreate}
            onCancel={() => setModal(false)}
          />
        </div>
      )}
    </>
  );
}
